import React, { useState, useMemo } from "react";
import DashboardCardWrapper from "../../common/DashboardCardWrapper";
import DashboardCardHeader from "../../common/DashboardCardHeader";
import BorderedIconWrapper from "../../common/BorderIconWrapper";
import { CardContent } from "../../ui/card";
import ReferralsLightIcon from "@/assets/svg/referralsLightIcon.svg";
import ReferralsDarkIcon from "@/assets/svg/referralsDarkIcon.svg";
import { DataTable } from "@/components/common/Table";
import { columns } from "./referralsColumn";
import { routes } from "@/utils/urls";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { Typography } from "@/components/ui/typography";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import type { Referrals, ReferralsResponse } from "./referrals.types";
import ReferralsDialog from "./ReferralsDialog";
import { Button } from "@/components/ui/button";
import { useFetchCard } from "@/hooks/useFetchCard";

interface ReferralsCardProps {
  shouldBlink?: boolean;
}

const ReferralsCard: React.FC<ReferralsCardProps> = ({
  shouldBlink = false,
}) => {
  const { cardVariant, isFavourite, toggleFavourite } =
    useDashboardCardFavorite("REFERRALS");
  const [isOpen, setIsOpen] = useState(false);

  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const {
    data: referralsResponse,
    loading,
    error,
    isErrorRed,
  } = useFetchCard<ReferralsResponse["data"]>({
    endpoint: `${routes.cards.referrals("").replace(/\/$/, "")}`,
    memberId,
    method: "POST",
    requestBody: { view: "card" },
    processData: (rawData: unknown) =>
      (rawData as ReferralsResponse).data || [],
  });

  const referralsData = useMemo(() => {
    if (!referralsResponse || referralsResponse.length === 0) return [];
    return referralsResponse.map(
      (item): Referrals => ({
        id: item.referralId,
        referralId: item.referralId,
        referralDate: item.referralDate,
        sendingFacility: item.sendingFacility,
        receivingFacility: item.receivingFacility,
        referralStatus: item.referralStatus,
        referralType: item.referralType || "",
      })
    );
  }, [referralsResponse]);

  const handleViewAll = () => {
    setIsOpen(true);
  };

  return (
    <>
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
        <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
          <DashboardCardHeader
            icon={
              <BorderedIconWrapper>
                <img
                  src={ReferralsLightIcon}
                  alt='ReferralsLightIcon'
                  className='w-4 h-4 block dark:hidden'
                />
                <img
                  src={ReferralsDarkIcon}
                  alt='ReferralsDarkIcon'
                  className='w-4 h-4 hidden dark:block'
                />
              </BorderedIconWrapper>
            }
            title='Referrals'
            className='h-[56px]'
            showStar={true}
            isFavourite={isFavourite}
            onToggleFavourite={toggleFavourite}
            button={
              <Button
                variant='button'
                className='!h-[28px] !px-3 !py-1 !text-sm mb-[2px]'
                onClick={handleViewAll}
                disabled={loading || !!error || referralsData.length === 0}>
                View all
              </Button>
            }
          />

          <CardContent className='p-4 lg:h-[520px] md:h-[520px]'>
            {error ? (
              <div className='flex items-center justify-center h-full lg:h-[520px] md:h-[520px]'>
                <Typography
                  variant='medium-14'
                  className={`${
                    isErrorRed
                      ? "text-red-500 dark:text-red-400"
                      : "text-grey-600 dark:text-grey-500"
                  } font-medium`}>
                  {error}
                </Typography>
              </div>
            ) : (
              <DataTable<Referrals>
                data={{
                  data: referralsData,
                  total: referralsData.length,
                }}
                columns={columns}
                height='auto'
                maxHeight='500px'
                isLoading={loading}
                skeletonCount={7}
              />
            )}
          </CardContent>
        </DashboardCardWrapper>
      </div>

      <ReferralsDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        memberId={memberId}
      />
    </>
  );
};

export default ReferralsCard;
